import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Edit, Trash2, Search, KeyRound, Phone, Mail, MapPin, X, AlertCircle, Copy, MessageCircle, BarChart3, Receipt, Calendar, TrendingUp, TrendingDown, Package, ChevronDown, ChevronUp, Layers, Tag } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const ClientsPanel = () => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | approved
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [codeDialog, setCodeDialog] = useState(null); // {client}
  const [detailClient, setDetailClient] = useState(null); // cliente cuyo detalle se está viendo

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch {
      toast.error('Error cargando clientes');
    } finally {
      setLoading(false);
    }
  };

  const filtered = clients.filter((c) => {
    const matchSearch = !search || ((c.name || '') + ' ' + (c.phone || '') + ' ' + (c.document || '') + ' ' + (c.access_code || '')).toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || c.registration_status === statusFilter;
    return matchSearch && matchStatus;
  });

  const pendingCount = clients.filter((c) => c.registration_status === 'pending').length;

  const handleSaveClient = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/clients/${editing.id}`, data);
        toast.success('Cliente actualizado');
      } else {
        await api.post('/clients', data);
        toast.success('Cliente creado');
      }
      setShowDialog(false);
      setEditing(null);
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando cliente');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este cliente?')) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Cliente desactivado');
      loadClients();
    } catch {
      toast.error('Error al desactivar');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold mb-1">Clientes</h2>
          <p className="text-xs md:text-sm text-white/60">
            {clients.length} clientes registrados
            {pendingCount > 0 && (
              <span className="ml-3 text-orange-400">
                · {pendingCount} pendiente(s) de aprobación
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowDialog(true); }}
          data-testid="add-client-btn"
          className="bg-white text-black hover:bg-gray-200 px-4 md:px-6 py-2.5 md:py-3 text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nuevo cliente
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, teléfono, código, cédula..."
            data-testid="clients-search"
            className="w-full bg-[#0A0A0A] border border-white/10 focus:border-white/30 pl-10 pr-3 py-2.5 text-sm outline-none"
          />
        </div>
        <div className="flex gap-2">
          {[
            { v: 'all', label: 'Todos', count: clients.length },
            { v: 'pending', label: 'Pendientes', count: pendingCount, accent: 'orange' },
            { v: 'approved', label: 'Aprobados', count: clients.length - pendingCount },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setStatusFilter(opt.v)}
              data-testid={`filter-${opt.v}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-wider border transition-colors ${
                statusFilter === opt.v
                  ? 'bg-white text-black border-white'
                  : opt.accent === 'orange' && opt.count > 0
                    ? 'border-orange-500/40 text-orange-400 hover:bg-orange-500/10'
                    : 'border-white/10 text-white/60 hover:border-white/30'
              }`}
            >
              {opt.label} ({opt.count})
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-center py-12 text-white/40">Cargando...</p>
      ) : filtered.length === 0 ? (
        <p className="text-center py-12 text-white/40 text-sm">No se encontraron clientes.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <ClientRow
              key={c.id}
              client={c}
              onEdit={() => { setEditing(c); setShowDialog(true); }}
              onAssignCode={() => setCodeDialog(c)}
              onDelete={() => handleDelete(c.id)}
              onViewDetail={() => setDetailClient(c)}
            />
          ))}
        </div>
      )}

      {showDialog && (
        <ClientDialog
          client={editing}
          onSave={handleSaveClient}
          onClose={() => { setShowDialog(false); setEditing(null); }}
        />
      )}

      {codeDialog && (
        <AssignCodeDialog
          client={codeDialog}
          onClose={() => setCodeDialog(null)}
          onSaved={() => { setCodeDialog(null); loadClients(); }}
        />
      )}

      {detailClient && (
        <ClientDetailDialog
          clientId={detailClient.id}
          onClose={() => setDetailClient(null)}
        />
      )}
    </div>
  );
};

const ClientRow = ({ client, onEdit, onAssignCode, onDelete, onViewDetail }) => {
  const isPending = client.registration_status === 'pending';
  const hasCode = !!client.access_code;
  const balance = client.balance || 0;

  return (
    <div
      data-testid={`client-row-${client.id}`}
      onClick={onViewDetail}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onViewDetail(); }}
      className={`bg-[#0A0A0A] border p-4 hover:border-white/30 hover:bg-white/[0.02] transition-colors cursor-pointer ${isPending ? 'border-orange-500/30' : 'border-white/10'}`}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-sm truncate">{client.name}</h3>
            {isPending && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-orange-500/20 text-orange-400 border border-orange-500/30">
                <AlertCircle className="w-2.5 h-2.5 inline mr-1" />
                Pendiente
              </span>
            )}
            {hasCode && (
              <span data-testid={`client-code-${client.id}`} className="text-xs font-mono tracking-widest px-2 py-0.5 bg-white/10 border border-white/20 rounded">
                {client.access_code}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider text-white/30 hidden md:inline">— Click para ver detalle</span>
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-white/50">
            {client.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{client.phone}</span>}
            {client.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{client.email}</span>}
            {client.document && <span>Doc: {client.document}</span>}
            {client.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{client.address}</span>}
          </div>
          {client.created_via === 'self' && (
            <p className="text-[10px] text-white/30 mt-1">Auto-registrado · {formatDate(client.created_at)}</p>
          )}
        </div>

        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
          <div className="text-right">
            <p className="text-[10px] text-white/40 uppercase tracking-wider">Saldo</p>
            <p className={`text-sm font-semibold ${balance < 0 ? 'text-red-400' : balance > 0 ? 'text-emerald-400' : 'text-white/60'}`}>
              ${Math.abs(balance).toLocaleString('es-CO', { maximumFractionDigits: 0 })}
            </p>
          </div>
          <div className="flex gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onAssignCode(); }}
              data-testid={`assign-code-${client.id}`}
              className={`p-2 border rounded transition-colors ${hasCode ? 'border-white/10 hover:border-white/30 text-white/70' : 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10'}`}
              title={hasCode ? 'Cambiar código' : 'Asignar código'}
            >
              <KeyRound className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              data-testid={`edit-client-${client.id}`}
              className="p-2 border border-white/10 hover:border-white/30 rounded"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              data-testid={`delete-client-${client.id}`}
              className="p-2 border border-white/10 hover:border-red-500/40 hover:text-red-400 rounded"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientDialog = ({ client, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: client?.name || '',
    phone: client?.phone || '',
    email: client?.email || '',
    document: client?.document || '',
    address: client?.address || '',
    notes: client?.notes || '',
    access_code: client?.access_code || '',
  });

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Nombre es obligatorio');
      return;
    }
    const payload = { ...form };
    if (payload.access_code) {
      const normalized = payload.access_code.toUpperCase().trim();
      if (!/^[A-Z]\d{4}$/.test(normalized)) {
        toast.error('Código inválido. Debe ser 1 letra + 4 dígitos (ej: A1234)');
        return;
      }
      payload.access_code = normalized;
    } else {
      payload.access_code = null;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">{client ? 'Editar cliente' : 'Nuevo cliente'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} data-testid="client-form" className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-white/60">Nombre *</label>
            <input type="text" value={form.name} onChange={handleChange('name')} required className="w-full bg-[#121212] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-white/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-white/60">Teléfono</label>
              <input type="tel" value={form.phone} onChange={handleChange('phone')} className="w-full bg-[#121212] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-white/30" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-white/60">Email</label>
              <input type="email" value={form.email} onChange={handleChange('email')} className="w-full bg-[#121212] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-white/30" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-white/60">Cédula / NIT</label>
              <input type="text" value={form.document} onChange={handleChange('document')} className="w-full bg-[#121212] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-white/30" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-white/60">
                Código de acceso <span className="text-white/30 normal-case">(opcional)</span>
              </label>
              <input
                type="text"
                value={form.access_code}
                onChange={(e) => setForm({ ...form, access_code: e.target.value.toUpperCase().slice(0, 5) })}
                placeholder="A1234"
                maxLength={5}
                className="w-full bg-[#121212] border border-emerald-500/20 focus:border-emerald-500/40 px-3 py-2.5 text-sm font-mono tracking-widest outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-white/60">Dirección</label>
            <input type="text" value={form.address} onChange={handleChange('address')} className="w-full bg-[#121212] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-white/30" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-white/60">Notas</label>
            <textarea value={form.notes} onChange={handleChange('notes')} rows="2" className="w-full bg-[#121212] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-white/30" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" data-testid="save-client-btn" className="flex-1 bg-white text-black hover:bg-gray-200 px-6 py-3 text-xs uppercase tracking-wider font-semibold">Guardar</button>
            <button type="button" onClick={onClose} className="flex-1 border border-white/20 hover:bg-white/5 px-6 py-3 text-xs uppercase tracking-wider">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignCodeDialog = ({ client, onClose, onSaved }) => {
  const [code, setCode] = useState(client?.access_code || '');
  const [saving, setSaving] = useState(false);

  const handleGenerate = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const l = letters[Math.floor(Math.random() * letters.length)];
    const n = String(Math.floor(1000 + Math.random() * 9000));
    setCode(l + n);
  };

  const handleSave = async () => {
    const normalized = code.trim().toUpperCase();
    if (normalized && !/^[A-Z]\d{4}$/.test(normalized)) {
      toast.error('Formato inválido. Debe ser 1 letra + 4 dígitos (ej: A1234)');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}/access-code`, { access_code: normalized || null });
      toast.success(normalized ? `Código ${normalized} asignado` : 'Código removido');
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar código');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyMessage = () => {
    if (!code) return;
    const msg = `Hola ${client.name}, tu código de acceso al Portal Clientes HAUTE es: ${code}\n\nIngresa en haute.company y haz tus pre-pedidos.`;
    navigator.clipboard.writeText(msg);
    toast.success('Mensaje copiado al portapapeles');
  };

  const handleWhatsApp = () => {
    if (!code || !client.phone) return;
    const cleanPhone = client.phone.replace(/[^0-9]/g, '');
    const msg = encodeURIComponent(`Hola ${client.name}, tu código de acceso al Portal Clientes HAUTE es: ${code}\n\nIngresa en haute.company y haz tus pre-pedidos.`);
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">Asignar código de acceso</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-white/60 mb-1">{client.name}</p>
        {client.phone && <p className="text-xs text-white/40 mb-6">{client.phone}</p>}

        <label className="block text-[10px] uppercase tracking-[0.2em] mb-2 text-white/60">Código (1 letra + 4 dígitos)</label>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 5))}
            placeholder="A1234"
            maxLength={5}
            data-testid="code-input"
            className="flex-1 bg-[#121212] border border-emerald-500/30 focus:border-emerald-500 px-3 py-3 text-xl text-center font-mono tracking-[0.5em] outline-none"
          />
          <button
            type="button"
            onClick={handleGenerate}
            className="px-4 py-3 border border-white/10 text-xs uppercase tracking-wider hover:bg-white/5"
            data-testid="generate-code"
          >
            Aleatorio
          </button>
        </div>
        <p className="text-xs text-white/40 mb-6">
          Deja vacío y guarda para remover el código.
        </p>

        {code && (
          <div className="flex gap-2 mb-4">
            <button onClick={handleCopyMessage} className="flex-1 border border-white/10 hover:bg-white/5 px-3 py-2.5 text-xs uppercase tracking-wider flex items-center justify-center gap-2">
              <Copy className="w-3.5 h-3.5" /> Copiar mensaje
            </button>
            {client.phone && (
              <button onClick={handleWhatsApp} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2.5 text-xs uppercase tracking-wider flex items-center justify-center gap-2">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </button>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving} data-testid="save-code-btn" className="flex-1 bg-white text-black hover:bg-gray-200 px-6 py-3 text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
            {saving ? 'Guardando...' : 'Guardar código'}
          </button>
          <button onClick={onClose} className="flex-1 border border-white/20 hover:bg-white/5 px-6 py-3 text-xs uppercase tracking-wider">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export const ClientDetailDialog = ({ clientId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('all'); // 'day' | 'week' | 'month' | 'year' | 'all'
  const [expandedInvoice, setExpandedInvoice] = useState(null); // invoice id
  const [productMap, setProductMap] = useState({}); // {product_id: {name, image, category}}
  const [zoomImage, setZoomImage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [detailRes, prodRes] = await Promise.all([
          api.get(`/clients/${clientId}`),
          api.get('/products', { params: { limit: 2000 } }).catch(() => ({ data: [] })),
        ]);
        setData(detailRes.data);
        // Build product map for thumbnails
        const map = {};
        (prodRes.data || []).forEach((p) => {
          map[p.id] = {
            name: p.name,
            image: p.images?.[0] || null,
            category: p.category,
            brand: p.brand,
          };
        });
        setProductMap(map);
      } catch {
        toast.error('Error cargando detalle');
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  const filterByRange = (items) => {
    if (range === 'all' || !items) return items || [];
    const now = new Date();
    const start = new Date(now);
    if (range === 'day') start.setDate(now.getDate() - 1);
    else if (range === 'week') start.setDate(now.getDate() - 7);
    else if (range === 'month') start.setMonth(now.getMonth() - 1);
    else if (range === 'year') start.setFullYear(now.getFullYear() - 1);
    return items.filter((it) => {
      const dt = new Date(it.created_at);
      return dt >= start && dt <= now;
    });
  };

  const filteredInvoices = useMemo(() => filterByRange(data?.invoices), [data, range]);
  const filteredPayments = useMemo(() => filterByRange(data?.payments), [data, range]);

  // Top categorías compradas en el período (cantidad y monto)
  const topCategories = useMemo(() => {
    const tally = {};
    filteredInvoices
      .filter((i) => i.status !== 'anulada')
      .forEach((inv) => {
        (inv.items_detail || inv.items || []).forEach((it) => {
          if (it.item_type === 'saldo_anterior') return;
          const cat = it.category_display || it.product_category || 'Sin categoría';
          if (!tally[cat]) {
            tally[cat] = { name: cat, slug: it.product_category, units: 0, amount: 0, products: new Set() };
          }
          tally[cat].units += it.quantity || 0;
          tally[cat].amount += it.subtotal || (it.quantity || 0) * (it.unit_price || 0);
          if (it.product_id) tally[cat].products.add(it.product_id);
        });
      });
    return Object.values(tally)
      .map((t) => ({ ...t, products: t.products.size }))
      .sort((a, b) => b.amount - a.amount);
  }, [filteredInvoices]);

  const totalPurchased = (data?.invoices || [])
    .filter((i) => i.status !== 'anulada')
    .reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = (data?.payments || []).reduce((s, p) => s + (p.amount || 0), 0);

  const rangePurchased = filteredInvoices
    .filter((i) => i.status !== 'anulada')
    .reduce((s, i) => s + (i.total || 0), 0);
  const rangePaid = filteredPayments.reduce((s, p) => s + (p.amount || 0), 0);

  const client = data?.client;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div
        data-testid="client-detail-dialog"
        className="bg-[#0A0A0A] border border-white/10 w-full max-w-4xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0A] z-10 border-b border-white/10 px-5 md:px-7 py-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">Detalle de cuenta</p>
            <h2 className="text-xl md:text-2xl font-semibold truncate">{client?.name}</h2>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {client?.access_code && (
                <span className="text-xs font-mono tracking-widest px-2 py-1 bg-white/10 border border-white/20">{client.access_code}</span>
              )}
              {client?.phone && <span className="text-xs text-white/50 flex items-center gap-1"><Phone className="w-3 h-3" />{client.phone}</span>}
              {client?.email && <span className="text-xs text-white/50 flex items-center gap-1"><Mail className="w-3 h-3" />{client.email}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20 text-white/40 text-sm">Cargando...</div>
        ) : (
          <div className="p-5 md:p-7 space-y-7">
            {/* Range filters */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="range-filters">
              <Calendar className="w-4 h-4 text-white/40" />
              {[
                { v: 'day', label: 'Hoy / 24h' },
                { v: 'week', label: 'Última semana' },
                { v: 'month', label: 'Último mes' },
                { v: 'year', label: 'Último año' },
                { v: 'all', label: 'Todo' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setRange(opt.v)}
                  data-testid={`range-${opt.v}`}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-wider border transition-colors ${
                    range === opt.v
                      ? 'bg-white text-black border-white'
                      : 'border-white/15 text-white/60 hover:border-white/40 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Stats: totales globales + filtrados */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <StatBlock
                label="Total comprado"
                value={range === 'all' ? totalPurchased : rangePurchased}
                sublabel={range === 'all' ? 'Histórico total' : `En el período · histórico: $${totalPurchased.toLocaleString('es-CO',{maximumFractionDigits:0})}`}
                icon={TrendingUp}
                accent="white"
                testid="stat-purchased"
              />
              <StatBlock
                label="Total pagado"
                value={range === 'all' ? totalPaid : rangePaid}
                sublabel={range === 'all' ? 'Histórico total' : `En el período · histórico: $${totalPaid.toLocaleString('es-CO',{maximumFractionDigits:0})}`}
                icon={TrendingDown}
                accent="green"
                testid="stat-paid"
              />
              <StatBlock
                label={(client?.balance || 0) < 0 ? 'Debe' : (client?.balance || 0) > 0 ? 'A favor' : 'Saldo'}
                value={Math.abs(client?.balance || 0)}
                sublabel="Saldo vigente"
                icon={Receipt}
                accent={(client?.balance || 0) < 0 ? 'red' : 'green'}
                testid="stat-balance"
              />
            </div>

            {/* Top Categorías más compradas */}
            {topCategories.length > 0 && (
              <section data-testid="top-categories">
                <h3 className="text-xs uppercase tracking-[0.25em] text-white/60 font-semibold mb-3 flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5" />
                  Categorías más compradas ({range === 'all' ? 'histórico' : 'en período'})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {topCategories.slice(0, 6).map((c, idx) => {
                    const maxAmount = topCategories[0].amount || 1;
                    const pct = Math.round((c.amount / maxAmount) * 100);
                    const isTop = idx === 0;
                    return (
                      <div
                        key={c.name}
                        data-testid={`top-cat-${c.slug || idx}`}
                        className={`relative bg-[#121212] border p-3 overflow-hidden ${isTop ? 'border-amber-500/40' : 'border-white/10'}`}
                      >
                        <div className="absolute inset-y-0 left-0 bg-amber-500/5" style={{ width: `${pct}%` }} />
                        <div className="relative">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-1.5 min-w-0">
                              {isTop && <span className="text-amber-400 text-xs">★</span>}
                              <p className="text-sm font-semibold capitalize truncate">{c.name.toLowerCase()}</p>
                            </div>
                            <span className="text-[10px] uppercase tracking-wider text-white/40">#{idx + 1}</span>
                          </div>
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="text-base font-mono font-semibold text-emerald-400 tabular-nums">
                              ${c.amount.toLocaleString('es-CO', { maximumFractionDigits: 0 })}
                            </p>
                            <p className="text-[10px] text-white/50">
                              <span className="text-white">{c.units}</span> u · <span className="text-white">{c.products}</span> prod
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Facturas */}
            <section data-testid="detail-invoices">
              <h3 className="text-xs uppercase tracking-[0.25em] text-white/60 font-semibold mb-3 flex items-center gap-2">
                <Receipt className="w-3.5 h-3.5" />
                Facturas ({filteredInvoices.length})
                <span className="text-[10px] normal-case tracking-normal text-white/40 ml-1">— Click para ver productos</span>
              </h3>
              {filteredInvoices.length === 0 ? (
                <p className="text-sm text-white/40 py-6 text-center border border-dashed border-white/10">Sin facturas en el período.</p>
              ) : (
                <div className="space-y-2">
                  {filteredInvoices.map((inv) => {
                    const isOpen = expandedInvoice === inv.id;
                    const items = (inv.items_detail || inv.items || []).filter((it) => it.item_type !== 'saldo_anterior');
                    const saldoAnterior = (inv.items_detail || inv.items || []).find((it) => it.item_type === 'saldo_anterior');
                    return (
                      <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className={`bg-[#121212] border transition-colors ${isOpen ? 'border-white/30' : 'border-white/5 hover:border-white/15'}`}>
                        <button
                          type="button"
                          onClick={() => setExpandedInvoice(isOpen ? null : inv.id)}
                          className="w-full p-3 md:p-4 flex items-center justify-between gap-3 flex-wrap text-left"
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-2 h-2 rounded-full ${inv.status === 'pagada' ? 'bg-emerald-500' : inv.status === 'parcial' ? 'bg-orange-500' : inv.status === 'anulada' ? 'bg-white/30' : 'bg-red-500'}`} />
                            <div>
                              <p className="text-sm">#{inv.invoice_number || inv.id.slice(0, 8)}</p>
                              <p className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">
                                {formatDate(inv.created_at)} · {items.length} productos
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-5 text-sm">
                            <div className="text-right">
                              <p className="text-[10px] uppercase tracking-wider text-white/40">Total</p>
                              <p className="font-semibold tabular-nums">${inv.total?.toLocaleString('es-CO', { maximumFractionDigits: 0 })}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] uppercase tracking-wider text-white/40">Saldo</p>
                              <p className={`tabular-nums ${inv.balance_due > 0 ? 'text-red-400 font-semibold' : 'text-white/40'}`}>
                                ${inv.balance_due?.toLocaleString('es-CO', { maximumFractionDigits: 0 })}
                              </p>
                            </div>
                            {isOpen ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                          </div>
                        </button>

                        {isOpen && (
                          <div data-testid={`invoice-detail-${inv.id}`} className="border-t border-white/10 p-3 md:p-4 bg-[#0A0A0A] space-y-3">
                            {/* Saldo anterior (si existe) */}
                            {saldoAnterior && (
                              <div className="bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs flex items-center justify-between">
                                <span className="uppercase tracking-wider text-amber-400">Saldo Anterior</span>
                                <span className="font-mono font-semibold tabular-nums text-amber-300">${saldoAnterior.subtotal?.toLocaleString('es-CO')}</span>
                              </div>
                            )}

                            {/* Items con thumbnails */}
                            {items.length === 0 ? (
                              <p className="text-xs text-white/40 text-center py-3">Sin productos en esta factura</p>
                            ) : (
                              <div className="space-y-2">
                                {items.map((it, ix) => {
                                  const prod = productMap[it.product_id];
                                  const img = prod?.image;
                                  return (
                                    <div key={ix} className="flex items-center gap-3 bg-[#121212] border border-white/5 p-2">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); if (img) setZoomImage(img); }}
                                        className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 bg-[#0A0A0A] border border-white/10 hover:border-white/40 rounded overflow-hidden relative group"
                                        data-testid={`invoice-item-img-${it.product_id}`}
                                      >
                                        {img ? (
                                          <>
                                            <img src={img} alt={it.product_name} className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                              <Search className="w-3 h-3 text-white" />
                                            </div>
                                          </>
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center text-white/20">
                                            <Package className="w-4 h-4" />
                                          </div>
                                        )}
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs sm:text-sm truncate">{it.product_name}</p>
                                        <p className="text-[10px] text-white/40 mt-0.5">
                                          {it.category_display || it.product_category}
                                          {it.sku && ` · ${it.sku}`}
                                        </p>
                                      </div>
                                      <div className="text-right flex-shrink-0">
                                        <p className="text-xs">
                                          <span className="text-white/60 tabular-nums">{it.quantity}</span>
                                          <span className="text-white/40 mx-1">×</span>
                                          <span className="text-white/60 tabular-nums">${it.unit_price?.toLocaleString('es-CO')}</span>
                                        </p>
                                        <p className="text-xs font-semibold font-mono tabular-nums text-emerald-400 mt-0.5">
                                          ${it.subtotal?.toLocaleString('es-CO')}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Totales */}
                            <div className="border-t border-white/10 pt-3 text-xs space-y-1">
                              {inv.subtotal !== undefined && inv.subtotal !== inv.total && (
                                <div className="flex justify-between text-white/50">
                                  <span>Subtotal</span>
                                  <span className="tabular-nums">${inv.subtotal?.toLocaleString('es-CO')}</span>
                                </div>
                              )}
                              {inv.discount > 0 && (
                                <div className="flex justify-between text-white/50">
                                  <span>Descuento</span>
                                  <span className="tabular-nums">-${inv.discount?.toLocaleString('es-CO')}</span>
                                </div>
                              )}
                              {inv.tax > 0 && (
                                <div className="flex justify-between text-white/50">
                                  <span>IVA</span>
                                  <span className="tabular-nums">${inv.tax?.toLocaleString('es-CO')}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-semibold text-sm pt-1 border-t border-white/5">
                                <span>Total</span>
                                <span className="tabular-nums">${inv.total?.toLocaleString('es-CO')}</span>
                              </div>
                              {inv.amount_paid > 0 && (
                                <div className="flex justify-between text-emerald-400 text-xs">
                                  <span>Pagado</span>
                                  <span className="tabular-nums">${inv.amount_paid?.toLocaleString('es-CO')}</span>
                                </div>
                              )}
                              {inv.balance_due > 0 && (
                                <div className="flex justify-between text-red-400 font-semibold">
                                  <span>Saldo pendiente</span>
                                  <span className="tabular-nums">${inv.balance_due?.toLocaleString('es-CO')}</span>
                                </div>
                              )}
                            </div>

                            {inv.notes && (
                              <p className="text-[11px] text-white/40 italic border-t border-white/10 pt-2">Notas: {inv.notes}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Pagos */}
            <section data-testid="detail-payments">
              <h3 className="text-xs uppercase tracking-[0.25em] text-white/60 font-semibold mb-3">
                Pagos ({filteredPayments.length})
              </h3>
              {filteredPayments.length === 0 ? (
                <p className="text-sm text-white/40 py-6 text-center border border-dashed border-white/10">Sin pagos en el período.</p>
              ) : (
                <div className="space-y-2">
                  {filteredPayments.map((p, idx) => (
                    <div key={p.id || idx} className="bg-[#121212] border border-emerald-500/15 p-3 md:p-4 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        <div>
                          <p className="text-sm">{formatDate(p.created_at)}</p>
                          {p.payment_method && <p className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">{p.payment_method}</p>}
                        </div>
                      </div>
                      <p className="text-base md:text-lg font-semibold text-emerald-400 tabular-nums">
                        +${p.amount?.toLocaleString('es-CO',{maximumFractionDigits:0})}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {/* Image zoom modal */}
      {zoomImage && (
        <div
          className="fixed inset-0 bg-black/95 z-[70] flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setZoomImage(null); }}
          data-testid="invoice-img-zoom"
        >
          <img src={zoomImage} alt="Vista ampliada" className="max-w-full max-h-full object-contain" />
          <button
            onClick={(e) => { e.stopPropagation(); setZoomImage(null); }}
            className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/80 rounded"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
};

const StatBlock = ({ label, value, sublabel, icon: Icon, accent = 'white', testid }) => {
  const colors = {
    red: 'text-red-400',
    green: 'text-emerald-400',
    white: 'text-white',
  };
  return (
    <div data-testid={testid} className="bg-[#121212] border border-white/10 p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${colors[accent]}`} />}
      </div>
      <p className={`text-2xl md:text-3xl font-semibold tabular-nums ${colors[accent]}`}>
        ${(value || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}
      </p>
      {sublabel && <p className="text-[10px] uppercase tracking-wider text-white/30 mt-2">{sublabel}</p>}
    </div>
  );
};

export default ClientsPanel;
